import React, { useCallback, useEffect, useState } from 'react';
import { fetchUatStatus, resetUatData, type UatResetResult, type UatStatus } from '../../data/api';

const CARD = 'rounded-2xl border border-[#1e6bb824] bg-white p-4 shadow-sm md:p-6';
const BTN = 'rounded-lg bg-avcs-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-avcs-primary focus-visible:ring-offset-2';
const BTN_QUIET = 'rounded-lg border border-[#1e6bb833] px-3 py-1.5 text-sm font-medium text-avcs-text transition hover:bg-[#1e6bb80f] focus:outline-none focus-visible:ring-2 focus-visible:ring-avcs-primary';
const ERROR_BOX = 'rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]';
const OK_BOX = 'rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]';
const CODE = 'rounded bg-[#1e6bb812] px-1.5 py-0.5 font-mono text-[13px] text-avcs-text';

type Props = { onOpenDeal: (dealId: string) => void; onOpenDocuments: (dealId: string) => void };

export default function UatConsole({ onOpenDeal, onOpenDocuments }: Props) {
  const [status, setStatus] = useState<UatStatus | null>(null);
  const [result, setResult] = useState<UatResetResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    fetchUatStatus().then(setStatus).catch(() => setError('Could not load the UAT status.'));
  }, []);
  useEffect(load, [load]);

  const reload = async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    try {
      const next = await resetUatData();
      setResult(next);
      setStatus(next);
    } catch {
      setError('The UAT data could not be reloaded. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!status) {
    return <section className={CARD}><p className="text-sm text-avcs-muted">{error || 'Loading…'}</p></section>;
  }
  if (!status.enabled) {
    return <section className={CARD}><p className="text-sm text-avcs-muted">UAT tools are switched off on this server.</p></section>;
  }

  return (
    <div className="space-y-4">
      <section className={CARD}>
        <h2 className="text-2xl font-semibold text-avcs-text">User acceptance testing</h2>
        <p className="mt-2 max-w-3xl text-sm text-avcs-muted">
          The UAT runs on its own fictitious producers and deals, so it never touches real data and does not
          depend on which deal numbers exist today. Reload the data whenever you want to start again: the
          test deals are rebuilt if they are missing, documents go back to their starting state, anything a
          farmer uploaded is removed, and the farmer logins get a new password.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className={BTN} onClick={reload} disabled={busy}>
            {busy ? 'Reloading…' : status.ready ? 'Reload UAT data' : 'Load UAT data'}
          </button>
          <span className="text-sm text-avcs-muted">Takes a few seconds. Only the UAT producers and deals are touched.</span>
        </div>
        {error && <div className={`${ERROR_BOX} mt-3`} role="alert">{error}</div>}
      </section>

      {result && (
        <section className={CARD} aria-live="polite">
          <div className={OK_BOX}>
            UAT data reloaded
            {result.created.length ? `: built ${result.created.length} deal${result.created.length === 1 ? '' : 's'}` : ': nothing was missing'}
            {result.documents_removed ? `, removed ${result.documents_removed} test document${result.documents_removed === 1 ? '' : 's'}` : ''}.
          </div>
          <h3 className="mt-4 text-lg font-semibold text-avcs-text">Farmer logins</h3>
          <p className="mt-1 text-sm text-avcs-muted">
            Both farmers share this password. It is shown now and replaced the next time you reload. Sign in to the
            farmer portal in a private window so you stay signed in here.
          </p>
          <dl className="mt-3 grid gap-2 text-sm md:grid-cols-[auto_1fr] md:gap-x-6">
            {(result.farmers ?? []).map((f) => (
              <React.Fragment key={f.username}>
                <dt className="text-avcs-muted">{f.producer}</dt>
                <dd><span className={CODE}>{f.username}</span></dd>
              </React.Fragment>
            ))}
            <dt className="text-avcs-muted">Password</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <span className={CODE}>{result.password}</span>
              <button type="button" className={BTN_QUIET} onClick={copyPassword}>{copied ? 'Copied' : 'Copy'}</button>
            </dd>
          </dl>
        </section>
      )}

      <section className={CARD}>
        <h3 className="text-lg font-semibold text-avcs-text">Where the UAT data is</h3>
        <p className="mt-1 text-sm text-avcs-muted">
          The UAT deals are numbered <span className={CODE}>UAT-…</span> and can be found by typing UAT in the deal
          list search. These buttons open them directly.
        </p>
        {status.ready ? (
          <ul className="mt-3 divide-y divide-[#1e6bb81a]">
            {(status.deals ?? []).map((deal) => (
              <li key={deal.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-avcs-text">{deal.deal_no} · {deal.producer}</div>
                  <div className="text-sm text-avcs-muted">{deal.description}</div>
                  <div className="text-xs text-avcs-muted">
                    {deal.animals} {deal.animals === 1 ? 'animal' : 'animals'} · {deal.documents} {deal.documents === 1 ? 'document' : 'documents'}, {deal.released} shown to the producer
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" className={BTN_QUIET} onClick={() => onOpenDeal(deal.id)}>Open deal</button>
                  <button type="button" className={BTN_QUIET} onClick={() => onOpenDocuments(deal.id)}>Documents</button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-avcs-muted">The UAT data is not loaded on this server yet. Use Load UAT data above.</p>
        )}
      </section>
    </div>
  );
}
